import { LitElement, html } from 'lit';
import { t } from '../i18n';
import type { FilterBarActionDetail, FilterCountMap } from './shared';
import { defineZenComponent } from './register';

const deadlineKeys = ['all', 'today', 'tomorrow', 'week', 'month', 'overdue', 'none'] as const;
const statusKeys = ['active', 'review', 'done'] as const;

export class ZenFilterBar extends LitElement {
  static properties = {
    deadlineFilter: { attribute: false },
    statusFilter: { attribute: false },
    counts: { attribute: false },
    doneHasMore: { attribute: false },
  };

  declare deadlineFilter: string;
  declare statusFilter: string;
  declare counts: FilterCountMap;
  /** #2739: the done count covers only the loaded page; older Goals remain. */
  declare doneHasMore: boolean;

  constructor() {
    super();
    this.deadlineFilter = 'all';
    this.statusFilter = 'active';
    this.doneHasMore = false;
    this.counts = {
      all: 0,
      today: 0,
      tomorrow: 0,
      week: 0,
      month: 0,
      overdue: 0,
      none: 0,
      active: 0,
      review: 0,
      done: 0,
    };
  }

  createRenderRoot() {
    return this;
  }

  private countLabel(value: number, hasMore = false) {
    if (hasMore) return `(${value}+)`;
    return value > 0 ? `(${value})` : '';
  }

  private emit(action: FilterBarActionDetail['action'], value: string) {
    this.dispatchEvent(new CustomEvent<FilterBarActionDetail>('filter-bar-action', {
      bubbles: true,
      composed: true,
      detail: { action, value },
    }));
  }

  private renderDeadlineButton(key: typeof deadlineKeys[number], labelKey: string) {
    const active = this.deadlineFilter === key;
    return html`
      <button class="filter-btn ${active ? 'active' : ''}" @click=${() => this.emit('deadline-filter-change', key)}>
        <span>${t(labelKey)}</span>
        <span class="filter-count">${this.countLabel(this.counts[key])}</span>
      </button>
    `;
  }

  private renderStatusButton(key: typeof statusKeys[number], labelKey: string, icon = '') {
    const active = this.statusFilter === key;
    return html`
      <button class="filter-btn ${active ? 'active' : ''}" @click=${() => this.emit('status-filter-change', key)}>
        ${icon ? html`<span class="mi mi-xs">${icon}</span>` : null}
        <span>${t(labelKey)}</span>
        <span class="filter-count">${this.countLabel(this.counts[key], key === 'done' && this.doneHasMore)}</span>
      </button>
    `;
  }

  render() {
    return html`
      <div class="filter-row">
        <span class="filter-bar-label">${t('filterLabelDeadline')}</span>
        ${this.renderDeadlineButton('all', 'filterAll')}
        ${this.renderDeadlineButton('today', 'filterToday')}
        ${this.renderDeadlineButton('tomorrow', 'filterTomorrow')}
        ${this.renderDeadlineButton('week', 'filterThisWeek')}
        ${this.renderDeadlineButton('month', 'filterThisMonth')}
        ${this.renderDeadlineButton('overdue', 'filterOverdue')}
        ${this.renderDeadlineButton('none', 'filterNone')}
      </div>
      ${/* #2856: the panel is 完了 in the menu, so the status row has nothing to switch. */ ''}
      ${this.statusFilter === 'done' ? null : html`<div class="filter-row">
        <span class="filter-bar-label">${t('filterLabelStatus')}</span>
        ${this.renderStatusButton('active', 'filterActive')}
        ${this.renderStatusButton('review', 'filterReview', 'rate_review')}
        ${this.renderStatusButton('done', 'filterDone')}
      </div>`}
    `;
  }
}

defineZenComponent('zen-filter-bar', ZenFilterBar);
